import MarkdownContent from "@/Components/MarkdownContent"
import PageTitle from "@/Components/PageTitle"
import { makeUTCDate, parseWebCOOSAsset } from "@/services/assets/parsers"
import { fetchAPIAsset, fetchWebCOOSCameraSummary } from "@/services/assets/services"
import type { IWebCOOSAssetSummaryView, IWebCOOSElement, IWebCOOSElementInventory, IWebCOOSParsedAsset, IWebCOOSParsedAssetService, IWebCOOSParsedGalleryService, IWebCOOSRawAssetServiceStream } from "@/services/assets/types"
import { useAPIContext } from "@/state/ApiContext"
import { Button, SelectInput, Tabs, Tooltip, ViewWithLoader } from "@axdspub/axiom-ui-utilities"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState, type ReactElement, type ReactNode } from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import LatestImage from "@/Components/Media/LatestImage"
import VideoPlayer from "@/Components/Media/VideoPlayer"
import { useWebCOOSCameraSummary } from "@/services/assets/useWebCOOSCameraSummary"
import MapDataLoader from "@/Components/Map/Map"
import GalleryGrid from "@/Components/Media/GalleryGrid"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid"
import { useWebCOOSElementInventory } from "@/services/assets/useWebCOOSPostgrest"
import { CalendarDemo, DatePickerDemo } from "@/Components/DatePicker/DatePicker"
import ImageLoader from "@/Components/Media/ImageLoader"
import { BugIcon, ChevronFirstIcon, ChevronLastIcon } from "lucide-react"
import StaticMap from "@/Components/Map/StaticMap"
import isMobileAtom from "@/state/isMobileAtom"
import { useAtom } from "jotai"


const oneDayInMS = 24 * 60 * 60 * 1000;


const InventoryView = ({
    service,
    selectedDate,
    setSelectedDate
}: {
    service: IWebCOOSParsedGalleryService,
    summary: IWebCOOSAssetSummaryView,
    detail: IWebCOOSParsedAsset,
    selectedDate?: Date,
    setSelectedDate: (date: Date | undefined) => void
}): ReactElement => {
    const apiContext = useAPIContext()
    const { data, isLoading, isFetching, error } = useWebCOOSElementInventory({
        ...apiContext,
        params: {
            filters: [
                {
                    column: 'service_slug',
                    operator: 'eq',
                    value: service?.common.slug ?? 'NA'
                }
            ],
            order: [
                {
                    column: 'time_bucket',
                    dir: 'desc'
                }
            ]
        },
        grouping: 'day'
    })


    const values = data?.map(d => d.bucket_count)?.filter(d => d > 0)?.sort((a, b) => a - b) ?? []
    const dataDomain: [number, number] | undefined = values.length > 0 ? [values[0], values[values.length - 1]] : undefined
    const byMonth: Record<string, IWebCOOSElementInventory[]> = {}
    if (data !== undefined) {
        data.forEach(d => {
            //const date = new Date(d.time_bucket)
            //const monthKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
            const monthKey = d.time_bucket.substring(0, 7)
            if (!byMonth[monthKey]) {
                byMonth[monthKey] = []
            }
            byMonth[monthKey].push(d)
        })
    }

    const sizeRange = [20, 40]
    const opacityRange = [0.2, 1]

    return (
        <ViewWithLoader data={data} isLoading={isLoading} isFetching={isFetching} error={error}>
            {data && data.length > 0 &&
                <>
                    <div className='bg-slate-100/80 h-15 sticky top-35 z-10 -mt-4 left-0 right-0 -mx-10'>
                        {
                            dataDomain?.length === 2
                                ? <div className='flex flex-row items-center justify-center gap-2 px-4 py-2'>
                                    <span className='text-sm'>Media files available:</span>
                                    <div className='flex flex-row items-center justify-center gap-2'>

                                        <span className='block rounded-4xl border-2 border-white bg-slate-600' style={{
                                            width: `${sizeRange[0]}px`,
                                            height: `${sizeRange[0]}px`,
                                            opacity: opacityRange[0]
                                        }}></span>
                                        <span className='text-xs'>{dataDomain[0]}</span>
                                        <span className='block rounded-4xl border-2 border-white bg-slate-600 ml-2' style={{
                                            width: `${sizeRange[1]}px`,
                                            height: `${sizeRange[1]}px`,
                                            opacity: opacityRange[1]
                                        }}></span>
                                        <span className='text-xs'>{dataDomain[1]}</span>
                                    </div>
                                </div>
                                : ''
                        }
                    </div>
                    <div className='grid sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mt-4'>
                        {Object.keys(byMonth).sort((a, b) => b.localeCompare(a)).map(monthKey => (
                            <div key={monthKey} className='border border-slate-300 rounded-md p-2'>
                                <CalendarDemo
                                    className='border-none shadow-none mx-auto'
                                    selectedDate={selectedDate}
                                    setSelectedDate={(date): void => {
                                        setSelectedDate(date)
                                    }}
                                    dataDomain={dataDomain}
                                    inventoryData={byMonth[monthKey]}
                                    start={new Date(`${monthKey}-01`)}
                                    end={new Date(`${monthKey}-31`)}
                                    calendarProps={{
                                        captionLayout: 'label',
                                        hideNavigation: true,
                                        showOutsideDays: false
                                    }}
                                />

                            </div>
                        ))}
                    </div>
                </>
            }
        </ViewWithLoader>
    )
}


const dateStringToDayDate = (dateString: string): Date => {
    const [year, month, day] = dateString?.split('T')[0].split('-').map(s => parseInt(s)) ?? []
    const date = new Date(year, month - 1, day);
    return date;
}



const MediaGalleries = ({
    detail,
    summary,
}: {
    detail: IWebCOOSParsedAsset,
    summary: IWebCOOSAssetSummaryView
}): ReactElement | null => {
    const galleryServices = detail.galleryServices
    //const urlParams = new URL(window.location.href).searchParams
    const [params] = useSearchParams()
    console.log('Service debug: MediaGalleries params', params)
    console.log('Service debug: MediaGalleries galleryServices', new URL(window.location.href).searchParams)
    const [service, setService] = useState<IWebCOOSParsedGalleryService | null>(
        params.get('service')
            ? galleryServices.find(g => g.uuid === params.get('service') || g.slug === params.get('service')) ?? (galleryServices.length > 0
                ? galleryServices[0]
                : null
            )
            : (galleryServices.length > 0
                ? galleryServices[0]
                : null
            )
    )
    const [selectedTab, setSelectedTab] = useState<string>(params.get('tab') ?? 'media')

    if (summary.asset_last_ending == null || summary.asset_last_ending === undefined) {
        return <p>No imagery available</p>
    }

    const lastEnding = new Date(makeUTCDate(summary.asset_last_ending)).toISOString()
    const timeZone = summary.asset_timezone
        ?? Intl.DateTimeFormat().resolvedOptions().timeZone
        ?? 'America/New_York';
    const lastDateInTZ = new Date(new Date(lastEnding).toLocaleString('en-US', { timeZone }))
    const lastDayInTZ = new Date(lastDateInTZ.getFullYear(), lastDateInTZ.getMonth(), lastDateInTZ.getDate(), 0, 0, 0)
    const dateParam = params.get('date');
    const paramDate = dateParam !== null && dateParam !== undefined
        ? new Date(dateParam.match(/T/) ? dateParam : dateParam + 'T00:00:00')
        : null
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(paramDate !== null && +paramDate <= +lastDayInTZ ? paramDate : lastDayInTZ)

    useEffect(() => {
        const url = new URL(window.location.href)
        if (service?.uuid !== undefined && service?.uuid !== null) {
            url.searchParams.set('service', service.uuid)
        } else {
            url.searchParams.delete('service')
        }
        const serviceStart = service?.elements.first_starting ? makeUTCDate(service.elements.first_starting) : undefined
        const serviceEnd = service?.elements.last_ending ? makeUTCDate(service.elements.last_ending) : new Date()
        if (serviceStart !== undefined && selectedDate !== undefined && +serviceStart > +selectedDate) {
            setSelectedDate(serviceStart)
        } else if (serviceEnd !== undefined && selectedDate !== undefined && +serviceEnd < +selectedDate) {
            setSelectedDate(new Date(+serviceEnd - oneDayInMS))
        }
        window.history.pushState({}, '', url.toString())
    }, [service])


    useEffect(() => {
        const url = new URL(window.location.href)
        if (selectedTab !== undefined && selectedTab !== null) {
            url.searchParams.set('tab', selectedTab)
        } else {
            url.searchParams.delete('tab')
        }
        window.history.pushState({}, '', url.toString())
    }, [selectedTab])

    useEffect(() => {
        const url = new URL(window.location.href)
        if (selectedDate !== undefined && selectedDate !== null) {
            url.searchParams.set('date', selectedDate.toISOString().split('T')[0])
            if (selectedTab !== 'media') {
                setSelectedTab('media')
            }
        } else {
            url.searchParams.delete('date')
        }
        window.history.pushState({}, '', url.toString())
    }, [selectedDate])

    const apiContext = useAPIContext()
    const { data: allData, isLoading, isFetching, error } = useWebCOOSElementInventory({
        ...apiContext,
        params: {
            filters: [
                {
                    column: 'service_slug',
                    operator: 'eq',
                    value: service?.common.slug ?? 'NA'
                }
            ],
            order: [
                {
                    column: 'time_bucket',
                    dir: 'desc'
                }
            ]
        },
        grouping: 'day'
    })


    const data = allData?.filter(d => {
        return d.bucket_count > 0
    })
    const sorted = data?.sort((a, b) => new Date(a.time_bucket).getTime() - new Date(b.time_bucket).getTime())
    const firstDay = sorted !== undefined && sorted.length > 0 ? dateStringToDayDate(sorted[0].time_bucket) : undefined
    const lastDay = sorted !== undefined && sorted.length > 0 ? dateStringToDayDate(sorted[sorted.length - 1].time_bucket) : undefined
    const [isMobile] = useAtom(isMobileAtom)


    const datePicker = <DatePickerDemo
        selectedDate={selectedDate ?? lastDayInTZ}
        inventoryData={data}
        setSelectedDate={setSelectedDate}


    />

    const galleryBrowse = data && <div className='text-sm flex flex-row flex-wrap items-center bg-slate-100 px-3 h-full'>
        <div className='flex-row gap-2 pt-3 pb-2 px-4'>
            <span className='mt-2'>{!isMobile ? 'Select gallery' : 'Gallery'}</span>
            <span className='inline-block ml-2'>
                <SelectInput
                    label={null}
                    className=' shadow-xs rounded-md bg-white'
                    size="sm"
                    includePrompt={false}
                    id="camera-detail-view-select"
                    testId="camera-detail-view-select"
                    value={service?.uuid ?? ''}
                    onChange={(e): void => {
                        const selectedService = galleryServices.find(g => g.uuid === e?.value) ?? null
                        setService(selectedService)

                    }}

                    options={
                        detail.galleryServices.map(g => {
                            return {
                                value: g.uuid,
                                label: g.label
                            }
                        })
                    }

                />
            </span>
        </div>
        <div className='flex flex-row gap-2 pt-3 pb-2 px-4 align-middle items-center'>
            <span>Select date</span>
            <div className='flex flex-row gap-1 items-center'>
                <Tooltip content="First" dark={true} useSpan={true}>
                    <Button
                        className="border-none"
                        size='xs'
                        disabled={firstDay === undefined || selectedDate === undefined || selectedDate.getTime() === +firstDay}
                        onClick={() => {
                            setSelectedDate(firstDay)
                        }}
                    ><ChevronFirstIcon className='h-6 w-6  inline' /></Button>
                </Tooltip>
                <Tooltip content="Previous" dark={true} useSpan={true}>
                    <Button
                        className="border-none"
                        size='xs'
                        disabled={firstDay === undefined || selectedDate === undefined || selectedDate.getTime() === +firstDay}
                        onClick={() => {
                            if (sorted !== undefined && selectedDate !== undefined) {
                                const index = sorted.findIndex(d => new Date(d.time_bucket).toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0])
                                const newDateString = index > 0 ? sorted[index - 1].time_bucket : undefined
                                if (newDateString !== undefined) {
                                    setSelectedDate(dateStringToDayDate(newDateString))
                                }
                            }
                        }}
                    ><ChevronLeftIcon className='h-6 w-6  inline' /></Button>
                </Tooltip>

                {
                    !isMobile && datePicker
                }
                <Tooltip content="Next" dark={true} useSpan={true}>
                    <Button
                        className="border-none"
                        size='xs'
                        disabled={lastDay === undefined || selectedDate === undefined || selectedDate.getTime() === +lastDay}
                        onClick={() => {
                            if (sorted !== undefined && selectedDate !== undefined) {
                                const index = sorted.findIndex(d => new Date(d.time_bucket).toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0])
                                const newDateString = index < (sorted.length - 1) ? sorted[index + 1].time_bucket : undefined
                                if (newDateString !== undefined) {
                                    setSelectedDate(dateStringToDayDate(newDateString))
                                }
                            }
                        }}
                    ><ChevronRightIcon className='h-6 w-6  inline' /></Button>
                </Tooltip>
                <Tooltip content="Last" dark={true} useSpan={true}>
                    <Button
                        className="border-none"
                        size='xs'
                        disabled={lastDay === undefined || selectedDate === undefined || selectedDate.getTime() === +lastDay}
                        onClick={() => {
                            if (selectedDate !== undefined) {
                                setSelectedDate(lastDay)
                            }
                        }}
                    ><ChevronLastIcon className='h-6 w-6  inline' /></Button>
                </Tooltip>
            </div>
            <Tooltip content="Debug view" dark={true} useSpan={true}>
                <Link to={`/demo/camera-detail/gallery-merge?camera_slug=${detail.slug}`}>
                    <BugIcon className='h-5 w-5 text-slate-600 inline ml-2' />
                </Link>
            </Tooltip>
        </div>
        {
            isMobile && (<div className='mb-4'>{datePicker}</div>)
        }
    </div>

    return (
        <div className='relative min-h-[50vh] mt-10'>
            <ViewWithLoader data={data} isLoading={isLoading} isFetching={isFetching} error={error}>
                {data !== undefined && data.length > 0 && service &&
                    <div>
                        <Tabs
                            navClassName="sticky top-22 bg-white -mx-10 px-12 shadow-md z-20 flex flex-row flex-wrap gap-4 items-center"
                            tabNavClassName="border-none flex-none"
                            activeTabNavClassName="border-none flex-none"
                            afterTabNavElements={galleryBrowse}
                            selectedTab={selectedTab}
                            onChange={(tab) => {
                                setSelectedTab(tab)
                            }}
                            tabs={[
                                {
                                    id: 'media',
                                    label: 'Media',
                                    content: <div className='relative'>
                                        <GalleryGrid
                                            service={service}
                                            summary={summary}
                                            end={new Date(+(selectedDate ?? lastDayInTZ) + oneDayInMS)}
                                            start={selectedDate ?? lastDayInTZ}
                                            ItemContainer={({
                                                children,
                                                element
                                            }: {
                                                children: ReactNode,
                                                element: IWebCOOSElement
                                            }): ReactElement => {
                                                return <Link
                                                    to={`/cameras/${detail.slug}/service/${service.uuid}/media/${element.uuid}`}
                                                >{children}</Link>
                                            }}
                                        />
                                    </div>
                                },
                                {
                                    id: 'inventory',
                                    label: 'Inventory',
                                    content: <>
                                        <InventoryView
                                            service={service}
                                            summary={summary}
                                            detail={detail}
                                            selectedDate={selectedDate}
                                            setSelectedDate={setSelectedDate}

                                        />
                                    </>
                                }
                            ]}
                        />
                    </div>
                }

            </ViewWithLoader>
        </div>
    )
}


export const CameraDetailMap = ({
    slug,
    detail
}: {
    slug: string,
    detail: IWebCOOSParsedAsset
}): ReactElement => {
    const navigate = useNavigate()


    return <><MapDataLoader
        id={slug}
        center={detail.longitude && detail.latitude ? { lon: detail.longitude, lat: detail.latitude } : undefined}
        zoom={detail.longitude && detail.latitude ? 10 : 2}
        LegendView={(): ReactElement => <></>}
        onItemSelect={(item): void => {
            navigate(`/cameras/${item?.asset_slug ?? ''}`)
        }}
        SelectView={(): ReactElement => <></>}
        selectedItemSlug={detail.slug}
        featureSort={(a, b) => {
            const aIsSelected = a.asset_slug === detail.slug ? 1 : 0
            const bIsSelected = b.asset_slug === detail.slug ? 1 : 0
            return aIsSelected - bIsSelected
        }}
        styleWedgeFn={(item, defaultProps): Record<string, unknown> => {
            return {
                ...defaultProps,
                ...(
                    item.asset_slug === detail.slug
                        ? {
                            opacity: .6
                        } : {
                            opacity: .2,
                            'stroke-width': 1
                        }
                )
            }
        }}
        stylePointFn={(item, defaultProps): Record<string, unknown> => {
            return {
                ...defaultProps,
                ...(
                    item.asset_slug === detail.slug
                        ? {
                            'point-radius': 12,
                            'fill-opacity': 1,
                            //opacity: .8

                        } : {
                            'point-radius': 4,
                            opacity: 1

                        }
                )
            }
        }}
    />
    </>

}


const CameraDetailView = ({
    detail,
    summary,
    stillImageService,
    liveStream,
    isLive

}: {
    detail: IWebCOOSParsedAsset,
    summary: IWebCOOSAssetSummaryView,
    stillImageService: IWebCOOSParsedAssetService | null,
    liveStream: IWebCOOSRawAssetServiceStream | null,
    isLive: boolean

}): ReactElement => {

    const mapHeight = isLive
        ? 350
        : 450

    return (
        <div className='p-10'>
            <PageTitle>{detail.label}</PageTitle>
            <h1 className='text-2xl font-bold sticky top-11 bg-white py-2 z-20'>
                <Link to='/cameras' className='text-primary hover:underline'>Cameras</Link>
                <ChevronRightIcon className='h-6 w-6 text-slate-400 inline mx-2' /> {detail.label}</h1>
            <div className='grid grid-cols-1 gap-1 p-2 lg:grid-cols-[2fr_6fr_3fr] md:gap-4 md:p-4 bg-primary-lighter'>
                {
                    detail.description &&
                    <MarkdownContent className="grow text-sm pb-4">{detail.description}</MarkdownContent>

                }
                <div>
                    {
                        isLive
                            ? liveStream !== null && liveStream !== undefined
                                ? <div className='h-full w-full'>
                                    <VideoPlayer
                                        options={{
                                            sources: [{
                                                src: liveStream.url
                                            }],
                                            autoPlay: true,
                                            controls: true,
                                            responsive: true,
                                            fluid: true,
                                            muted: true
                                        }} />
                                </div>
                                : stillImageService && <LatestImage
                                    asset={summary}
                                    imageClassName='object-cover'
                                    service={stillImageService}
                                    serviceSlug={stillImageService.data.common.slug}
                                    assetLabel={detail.label}
                                    defaultSize='medium'
                                    makeLink={(element, asset) => {
                                        return `/cameras/${asset.asset_slug}/service/${stillImageService.uuid}/media/${element.uuid}`
                                    }}
                                />
                            : <ImageLoader url={detail.thumbnail} label={detail.label} className="w-full h-auto" />

                    }
                </div>
                <div className={`hidden lg:flex flex-col gap-4 h-full`}>
                    {/* <div style={{ height: `${mapHeight}px` }} className="flex flex-col gap-2 items-center relative">
                        <CameraDetailMap detail={detail} slug={detail.slug} />
                    </div> */}
                    <StaticMap
                        longitude={detail.longitude}
                        latitude={detail.latitude}

                        height={mapHeight}
                        mapboxAccessToken={import.meta.env.VITE_PUBLIC_MAPBOX_TOKEN}
                        wedgePolygon={detail.wedge ?? undefined}
                        extraClasses="shadow-md" />
                    {
                        isLive
                            ? <div><ImageLoader url={detail.thumbnail} label={detail.label} className="shadow-md" /></div>
                            : null
                    }



                </div>

            </div>


            <MediaGalleries
                detail={detail}
                summary={summary}
            />
        </div>
    )

}

export const CameraSummaryLoader = ({
    slug,
    View
}: {
    slug: string,
    View: React.FC<{
        summary: IWebCOOSAssetSummaryView
    }>
}): ReactElement => {
    const { data, isLoading, error } = useWebCOOSCameraSummary(slug)

    return <ViewWithLoader isLoading={isLoading} data={data} error={error}>
        {
            data !== undefined && (
                <View summary={data} />
            )
        }
    </ViewWithLoader>


}


const CameraDetail = ({
    slug,
    View = CameraDetailView
}: {
    slug: string
    View?: React.FC<{
        detail: IWebCOOSParsedAsset,
        summary: IWebCOOSAssetSummaryView,
        stillImageService: IWebCOOSParsedAssetService | null,
        liveStream: IWebCOOSRawAssetServiceStream | null,
        isLive: boolean
    }>
}): ReactElement => {
    const apiContext = useAPIContext()
    console.log('CameraDetail loading slug', slug)
    const { data, isLoading, error } = useQuery({
        queryKey: ['camera', slug],
        queryFn: async ({ signal, queryKey }): Promise<{
            detail: IWebCOOSParsedAsset,
            summary: IWebCOOSAssetSummaryView
        } | undefined> => {
            console.log('CameraDetail fetching slug', queryKey)
            const summary = await fetchWebCOOSCameraSummary({
                ...apiContext,
                signal,
                slug: slug ?? 'na'
            })
            const detail = await fetchAPIAsset({
                ...apiContext,
                slug: slug ?? 'na',
                signal
            })
            return summary !== undefined && detail !== undefined
                ? {
                    detail: parseWebCOOSAsset(detail),
                    summary
                }
                : undefined
        }
    })

    const detail = data?.detail
    const summary = data?.summary
    const hasRecentData = detail?.dateBounds[1] !== null && detail?.dateBounds[1] !== undefined && (new Date(detail.dateBounds[1])).getTime() > (Date.now() - 1000 * 60 * 60 * 24 * 60)
    const liveStream = detail?.hls_stream ?? detail?.dash_stream
    const stillImageService = hasRecentData && detail.stillImageService ? detail.stillImageService : null
    const isLive = summary?.asset_disposition_slug === 'up'


    return (
        <>

            <ViewWithLoader isLoading={isLoading} data={data} error={error} keepExistingContentWhileLoading={false}>
                {
                    data !== undefined && (
                        <View detail={detail!} summary={summary!} stillImageService={stillImageService} liveStream={liveStream ?? null} isLive={isLive} />
                    )
                }
            </ViewWithLoader>
        </>
    )
}

export const CameraAssetDetail = ({
    slug,
    View = ({ summary }): ReactElement => {
        return <div>
            Override `View`. Asset summary for {summary.asset_label} loaded
            <pre className='text-xs bg-slate-200 p-10'>
                {JSON.stringify(summary, null, 2)}
            </pre>

        </div>
    }
}: {
    slug: string
    View?: React.FC<{
        summary: IWebCOOSAssetSummaryView
    }>
}): ReactElement => {
    const apiContext = useAPIContext()
    const { data, isLoading, error } = useQuery({
        queryKey: ['camera-summary', slug],
        queryFn: async ({ signal }): Promise<IWebCOOSAssetSummaryView> => {
            const summary = await fetchWebCOOSCameraSummary({
                ...apiContext,
                signal,
                slug: slug ?? 'na'
            })
            return summary
        }
    })


    return (
        <>

            <ViewWithLoader isLoading={isLoading} data={data} error={error}>
                {
                    data !== undefined && (
                        <View summary={data!} />
                    )
                }
            </ViewWithLoader>
        </>
    )
}

export default CameraDetail